import { createMessage, SB } from '@play-co/replicant';
import createMessages from '../config/messages/createMessages';
import { friendshipRequest } from './friends.schema';
import {
  addFriend,
  cleanupFriendshipRequests,
  markUnfriended,
} from './friends.modifiers';
import { isBuruFriend, wasUnfriendedBy } from './friends.getters';

export default createMessages({
  /**
   * Create friend request to target user
   * Executed on target user state
   */
  createFriendshipRequest: createMessage(
    friendshipRequest,
    (state, request, info) => {
      cleanupFriendshipRequests(state, info.timestamp);

      // The friends list is the only record of an accepted request: an existing friend has nothing to request.
      if (isBuruFriend(state, request.owner.id)) return;

      const isNewRequest = !state.friendshipRequests[request.owner.id];
      const isResentRequest = wasUnfriendedBy(state, request.owner.id);

      if (isNewRequest || isResentRequest) {
        state.friendshipRequests[request.owner.id] = request;
        state.friendshipRequests[request.owner.id].receivedAt = 0;
      }
    },
  ),

  /**
   * Accept friendship request from owner user
   * Executed on owner user state
   */
  acceptFriendshipRequest: createMessage(
    SB.object({}),
    (state, _, { senderId, timestamp }) => {
      cleanupFriendshipRequests(state, timestamp);

      if (!state.friendshipRequests[senderId]) {
        return; // TODO: Report warning, but how?
      }

      // Change state of request
      state.friendshipRequests[senderId].status = 'accepted';
      // Flag for once-only sender-side `FriendAdd` emission on next login
      state.friendshipRequests[senderId].senderAcknowledgedAt = 0;

      // Get target profile
      const { target } = state.friendshipRequests[senderId];

      // Add to GCinstant friends list
      addFriend(state, target.id, timestamp);
    },
  ),

  /**
   * Reject friendship request from owner user
   * Executed on owner user state
   */
  rejectFriendshipRequest: createMessage(
    SB.object({}),
    (state, _, { senderId, timestamp }) => {
      cleanupFriendshipRequests(state, timestamp);

      if (!state.friendshipRequests[senderId]) {
        return; // TODO: Report warning, but how?
      }

      // Change state of request
      state.friendshipRequests[senderId].status = 'rejected';
    },
  ),

  /**
   * Remove friendship entirely from the buruFriends state
   */
  removeFriend: createMessage(
    SB.object({}),
    (state, _, { senderId, timestamp }) => {
      cleanupFriendshipRequests(state, timestamp);

      // Remove request if present
      if (state.friendshipRequests[senderId]) {
        delete state.friendshipRequests[senderId];
      }

      // Remove friend from social friends list
      if (state.buruFriends) {
        delete state.buruFriends[senderId];
      }
    },
  ),

  /**
   * Mark friend as unfriended, but still keep in the state.
   * The recipient will no longer be able to see, send invitation, nor interact with the sender.
   * Only the sender can re-invite the recipient to undo the unfriending.
   */
  unfriend: createMessage(
    SB.object({}),
    (state, _, { senderId, timestamp }) => {
      // Mark sender as unfriended
      markUnfriended(state, senderId, timestamp);
    },
  ),

  /**
   * Automatically add friend from invite (Add Friends V2 flow).
   * When recipient lands via invite link, automatically add them as friend on inviter's side.
   * Also cleans up any existing friendship requests between the two users.
   */
  addFriendFromInvite: createMessage(
    SB.object({ friendId: SB.string() }),
    (state, args, info) => {
      // Clean up any existing friendship request from recipient to inviter
      if (state.friendshipRequests[args.friendId]) {
        delete state.friendshipRequests[args.friendId];
      }

      // Automatically add the friend without requiring approval
      addFriend(state, args.friendId, info.timestamp);
    },
  ),
});
